import * as React from 'react'
import { Loader2, Plus, RotateCcw, Trash2 } from 'lucide-react'
import {
  SOLUTION_PRICING_BASES,
  SOLUTION_PRICING_BASIS_LABELS,
  SOLUTION_PRICING_RATE_UNITS,
  type SolutionPricingBasis,
} from '@lib/enums/solution-pricing-basis.enum'
import type { ProblemSolutionDto } from '@lib/dto/problem-solution.dto'
import type { TreeProblemTypeDto } from '@lib/dto/tree-problem-type.dto'
import { formatMoneyAmount } from '@lib/helpers/money'
import {
  SEASONS,
  SEASON_LABELS,
  seasonSchema,
  type Season,
} from '@lib/enums/season.enum'
import { createProblemSolutionInputSchema } from '@lib/input/problem-solution.input'
import {
  useArchiveProblemSolution,
  useCreateProblemSolution,
  useUpdateProblemSolution,
} from '@/hooks/useTreeProblemTypes'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SELECT_CLASS } from '@/components/ui/field-classes'
import { errorMessage } from '@/helpers/logger'
import { cn } from '@/lib/utils'

/**
 * A rate while it is being typed. Blank is allowed here, including on a
 * basis that cannot be saved blank: the input schema refuses that on submit.
 * A saved solution is a `SolutionPricingDto`, which only allows a blank rate
 * for time.
 */
interface DraftPricing {
  basis: SolutionPricingBasis
  unitRate: string | null
}

interface Draft {
  label: string
  description: string
  pricings: DraftPricing[]
  usualSeason: Season | null
}

const EMPTY: Draft = {
  label: '',
  description: '',
  pricings: [],
  usualSeason: null,
}

function toDraft(solution: ProblemSolutionDto): Draft {
  return {
    label: solution.label,
    description: solution.description ?? '',
    pricings: solution.pricings,
    usualSeason: solution.usualSeason,
  }
}

/** How a solution is charged, in a phrase: "Time · $85/hr, Linear feet · $4.50/ft". */
function describePricing(solution: ProblemSolutionDto): string {
  if (solution.pricings.length === 0) return 'No charge'
  return solution.pricings
    .map((pricing) => {
      const unit = SOLUTION_PRICING_RATE_UNITS[pricing.basis]
      const rate =
        pricing.unitRate === null
          ? 'crew rate'
          : `${formatMoneyAmount(pricing.unitRate)} ${unit}`
      return `${SOLUTION_PRICING_BASIS_LABELS[pricing.basis]} · ${rate}`
    })
    .join(', ')
}

/**
 * Pick the bases a solution is charged on, and set a rate for each.
 *
 * A checkbox per basis rather than a single choice, because a solution may be
 * charged on more than one at once — cable bracing is labor by the hour plus
 * cable by the foot, and that is the case this shape exists for.
 *
 * Time is the one basis whose rate may be left blank, and blank there means
 * "use the crew rate from Pricing above". Leaving the others blank would price
 * the work at nothing, so the form asks for a number.
 */
function PricingFields({
  pricings,
  onChange,
}: {
  pricings: DraftPricing[]
  onChange: (next: DraftPricing[]) => void
}) {
  function toggle(basis: SolutionPricingBasis, on: boolean): void {
    onChange(
      on
        ? [...pricings, { basis, unitRate: null }]
        : pricings.filter((pricing) => pricing.basis !== basis),
    )
  }

  function setRate(basis: SolutionPricingBasis, unitRate: string): void {
    onChange(
      pricings.map((pricing) =>
        pricing.basis === basis
          ? { ...pricing, unitRate: unitRate === '' ? null : unitRate }
          : pricing,
      ),
    )
  }

  return (
    <div className="space-y-1.5">
      <Label>Charged by</Label>
      <div className="space-y-1.5">
        {SOLUTION_PRICING_BASES.map((basis) => {
          const chosen = pricings.find((pricing) => pricing.basis === basis)
          return (
            <div key={basis} className="flex flex-wrap items-center gap-2">
              <label className="flex w-44 shrink-0 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={chosen !== undefined}
                  onChange={(event) => {
                    toggle(basis, event.target.checked)
                  }}
                />
                {SOLUTION_PRICING_BASIS_LABELS[basis]}
              </label>
              {chosen === undefined ? null : (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-text">$</span>
                  <Input
                    className="w-24"
                    inputMode="decimal"
                    value={chosen.unitRate ?? ''}
                    placeholder={basis === 'TIME' ? 'crew rate' : ''}
                    onChange={(event) => {
                      setRate(basis, event.target.value)
                    }}
                  />
                  <span className="text-xs text-muted-text">
                    {SOLUTION_PRICING_RATE_UNITS[basis]}
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>
      <p className="text-xs text-muted-text">
        Leave the time rate blank to use the crew rate set under Pricing.
        Trunk diameter is read from the tree, so nobody types it twice.
      </p>
    </div>
  )
}

function SolutionForm({
  initial,
  isSaving,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial: Draft
  isSaving: boolean
  submitLabel: string
  onCancel: () => void
  onSubmit: (draft: Draft) => void
}) {
  const [draft, setDraft] = React.useState<Draft>(initial)

  return (
    <div className="space-y-2.5">
      <div className="space-y-1.5">
        <Label htmlFor="solution-label">Label</Label>
        <Input
          id="solution-label"
          value={draft.label}
          placeholder="Prune infected limbs"
          onChange={(event) => {
            setDraft({ ...draft, label: event.target.value })
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="solution-description">Description</Label>
        <textarea
          id="solution-description"
          rows={2}
          className="flex w-full rounded-md border border-default bg-surface px-2.5 py-1.5 text-sm text-primary-text transition-colors placeholder:text-disabled-text hover:border-strong focus-visible:outline-none"
          value={draft.description}
          placeholder="What the work involves, in a client's language."
          onChange={(event) => {
            setDraft({ ...draft, description: event.target.value })
          }}
        />
      </div>
      <PricingFields
        pricings={draft.pricings}
        onChange={(pricings) => {
          setDraft({ ...draft, pricings })
        }}
      />
      <div className="space-y-1.5">
        <Label htmlFor="solution-season">Usual season</Label>
        <select
          id="solution-season"
          className={SELECT_CLASS}
          value={draft.usualSeason ?? ''}
          onChange={(event) => {
            const parsed = seasonSchema.safeParse(event.target.value)
            setDraft({
              ...draft,
              usualSeason: parsed.success ? parsed.data : null,
            })
          }}
        >
          <option value="">None, goes with the pruning</option>
          {SEASONS.map((season) => (
            <option key={season} value={season}>
              {SEASON_LABELS[season]}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-text">
          Where a surveyor choosing it starts, and changes on the tree if
          needed: dormant oil in winter, bark banding in spring.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={isSaving}
          onClick={() => {
            onSubmit(draft)
          }}
        >
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {submitLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

/**
 * The treatments offered for one problem, and what each costs.
 *
 * Nested under the problem it belongs to rather than kept in a catalog of
 * its own: the question a surveyor is asking under a tree is "what do we do
 * about mistletoe", and the list that answers it is the list configured here.
 */
export function ProblemSolutionsEditor({
  problemType,
}: {
  problemType: TreeProblemTypeDto
}) {
  const createSolution = useCreateProblemSolution()
  const updateSolution = useUpdateProblemSolution()
  const archiveSolution = useArchiveProblemSolution()

  const [isAdding, setIsAdding] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  function validate(draft: Draft): Draft | null {
    setError(null)
    const parsed = createProblemSolutionInputSchema.safeParse({
      label: draft.label,
      description: draft.description,
      pricings: draft.pricings,
      usualSeason: draft.usualSeason,
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the details')
      return null
    }
    return {
      label: parsed.data.label,
      description: parsed.data.description ?? '',
      pricings: parsed.data.pricings,
      usualSeason: parsed.data.usualSeason ?? null,
    }
  }

  async function handleArchive(
    solution: ProblemSolutionDto,
  ): Promise<void> {
    setError(null)
    try {
      await archiveSolution.mutateAsync({
        id: solution.id,
        archived: solution.archivedAt === null,
      })
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  return (
    <div className="space-y-2 border-t border-default bg-subtle px-3 py-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-secondary-text">
          Solutions for {problemType.name}
        </p>
        {isAdding ? null : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setError(null)
              setEditingId(null)
              setIsAdding(true)
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add solution
          </Button>
        )}
      </div>

      {error === null ? null : <p className="text-sm text-danger">{error}</p>}

      {isAdding ? (
        <div className="rounded-md border border-default bg-surface px-3 py-3">
          <SolutionForm
            initial={EMPTY}
            isSaving={createSolution.isPending}
            submitLabel="Add solution"
            onCancel={() => {
              setIsAdding(false)
            }}
            onSubmit={(draft) => {
              const valid = validate(draft)
              if (valid === null) return
              void (async () => {
                try {
                  await createSolution.mutateAsync({
                    problemTypeId: problemType.id,
                    input: {
                      label: valid.label,
                      description: valid.description,
                      pricings: valid.pricings,
                      usualSeason: valid.usualSeason,
                    },
                  })
                  setIsAdding(false)
                } catch (caught) {
                  setError(errorMessage(caught, 'That could not be saved.'))
                }
              })()
            }}
          />
        </div>
      ) : null}

      {problemType.solutions.length === 0 && !isAdding ? (
        <p className="text-xs text-muted-text">
          None yet. Without one, this problem can be recorded and explained but
          not priced.
        </p>
      ) : null}

      <ul className="space-y-1">
        {problemType.solutions.map((solution) => (
          <li
            key={solution.id}
            className={cn(
              'rounded-md border border-default bg-surface px-3 py-2',
              solution.archivedAt === null ? null : 'opacity-55',
            )}
          >
            {editingId === solution.id ? (
              <SolutionForm
                initial={toDraft(solution)}
                isSaving={updateSolution.isPending}
                submitLabel="Save solution"
                onCancel={() => {
                  setEditingId(null)
                }}
                onSubmit={(draft) => {
                  const valid = validate(draft)
                  if (valid === null) return
                  void (async () => {
                    try {
                      await updateSolution.mutateAsync({
                        id: solution.id,
                        input: {
                          label: valid.label,
                          description: valid.description,
                          pricings: valid.pricings,
                          usualSeason: valid.usualSeason,
                        },
                      })
                      setEditingId(null)
                    } catch (caught) {
                      setError(errorMessage(caught, 'That could not be saved.'))
                    }
                  })()
                }}
              />
            ) : (
              <div className="flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {solution.label}
                    {solution.archivedAt === null ? null : (
                      <span className="ml-2 text-xs font-normal text-muted-text">
                        Archived
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-muted-text">
                    {describePricing(solution)}
                    {solution.usualSeason === null
                      ? null
                      : ` · usually ${SEASON_LABELS[solution.usualSeason].toLowerCase()}`}
                  </span>
                </span>
                {solution.archivedAt === null ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setError(null)
                      setIsAdding(false)
                      setEditingId(solution.id)
                    }}
                  >
                    Edit
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={archiveSolution.isPending}
                  aria-label={
                    solution.archivedAt === null
                      ? `Archive ${solution.label}`
                      : `Restore ${solution.label}`
                  }
                  onClick={() => {
                    void handleArchive(solution)
                  }}
                >
                  {solution.archivedAt === null ? (
                    <Trash2 className="h-3.5 w-3.5" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
